import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const CreateStaffModal = ({ onClose, onUserCreated, tenantId }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    phone: '',
    role: 'staff'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate form
      if (!formData?.fullName || !formData?.email || !formData?.password) {
        throw new Error('Please fill in all required fields');
      }

      if (formData?.password?.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      // Step 1: Create user in Supabase Auth
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.fullName,
            phone: formData?.phone || '',
            role: formData?.role
          }
        }
      });

      if (authError) throw authError;

      // Step 2: Wait for trigger to create user_profiles
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 3: Create tenant membership
      const { error: membershipError } = await supabase?.from('tenant_members')?.insert({
        tenant_id: tenantId,
        user_id: authData?.user?.id,
        role: formData?.role,
        membership_plan: null,
        is_active: true
      });

      if (membershipError) throw membershipError;

      onUserCreated?.();
    } catch (err) {
      setError(err?.message || 'Failed to create staff member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Icon name="Briefcase" size={24} color="var(--color-success)" />
            Create Staff Member
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          {/* Staff Role Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Staff Role <span className="text-destructive">*</span>
            </label>
            <select
              value={formData?.role}
              onChange={(e) => handleChange('role', e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            >
              <option value="staff">Front Desk Staff</option>
              <option value="trainer">Trainer</option>
              <option value="gym_owner">Manager/Owner</option>
            </select>
            <p className="text-xs text-muted-foreground mt-2">
              {formData?.role === 'staff' && 'Front desk staff can manage check-ins, POS, and member services'}
              {formData?.role === 'trainer' && 'Trainers can manage classes, workouts, and member coaching'}
              {formData?.role === 'gym_owner' && 'Managers have full access to all gym operations and settings'}
            </p>
          </div>

          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  value={formData?.fullName}
                  onChange={(e) => handleChange('fullName', e?.target?.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleChange('phone', e?.target?.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Account Credentials */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Account Credentials</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address <span className="text-destructive">*</span>
                </label>
                <Input
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleChange('email', e?.target?.value)}
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Temporary Password <span className="text-destructive">*</span>
                </label>
                <Input
                  type="password"
                  value={formData?.password}
                  onChange={(e) => handleChange('password', e?.target?.value)}
                  placeholder="Min. 6 characters"
                  required
                />
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex gap-3">
            <Icon name="Shield" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
            <div className="text-sm text-foreground">
              <p className="font-semibold mb-1">Access Control</p>
              <p className="text-muted-foreground">
                Staff members will have role-based access to gym management features. They can change their password after first login.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={20} className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Icon name="Briefcase" size={20} />
                  Create Staff
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateStaffModal;